import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import {
  listActiveAnnouncements,
  type Announcement,
  type ViewerAudience,
} from "@/lib/announcements";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const DISMISS_KEY = "announcement_dismissed_v1";

function loadDismissed(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(DISMISS_KEY) || "{}");
  } catch {
    return {};
  }
}
function markDismissed(id: string) {
  const cur = loadDismissed();
  cur[id] = Date.now();
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify(cur));
  } catch {}
}

export function AnnouncementDisplay({ audience }: { audience: ViewerAudience }) {
  const { data: items = [] } = useQuery({
    queryKey: ["announcements", "active", audience],
    queryFn: () => listActiveAnnouncements(audience),
    staleTime: 60_000,
  });
  const [dismissed, setDismissed] = useState<Record<string, number>>(() => loadDismissed());

  const visible = items.filter((a) => !dismissed[a.id]);
  const banner = visible.find((a) => a.mode === "banner");
  const popup = visible.find((a) => a.mode === "popup");

  return (
    <>
      {banner && (
        <BannerView
          a={banner}
          onDismiss={() => {
            markDismissed(banner.id);
            setDismissed(loadDismissed());
          }}
        />
      )}
      {popup && (
        <PopupView
          a={popup}
          onDismiss={() => {
            markDismissed(popup.id);
            setDismissed(loadDismissed());
          }}
        />
      )}
    </>
  );
}

export function BannerView({ a, onDismiss }: { a: Announcement; onDismiss: () => void }) {
  const bg = a.bg_color || "hsl(var(--primary))";
  const fg = a.text_color || "hsl(var(--primary-foreground))";
  return (
    <div
      className="w-full px-4 py-2 text-sm flex items-center justify-center gap-3"
      style={{ background: bg, color: fg }}
    >
      <span className="font-medium">{a.title}</span>
      {a.body && <span className="opacity-90 hidden sm:inline">{a.body}</span>}
      {a.cta_url && a.cta_label && (
        <a
          href={a.cta_url}
          target={a.cta_url.startsWith("http") ? "_blank" : undefined}
          rel="noreferrer"
          className="underline font-semibold"
        >
          {a.cta_label}
        </a>
      )}
      {a.dismissible && (
        <button
          onClick={onDismiss}
          className="ml-auto opacity-80 hover:opacity-100"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

export function PopupView({ a, onDismiss }: { a: Announcement; onDismiss: () => void }) {
  const [open, setOpen] = useState(true);
  useEffect(() => {
    setOpen(true);
  }, [a.id]);
  const close = () => {
    setOpen(false);
    onDismiss();
  };
  const bg = a.bg_color || undefined;
  const fg = a.text_color || undefined;
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) close();
      }}
    >
      <DialogContent style={{ background: bg, color: fg }}>
        <DialogHeader>
          <DialogTitle>{a.title}</DialogTitle>
          {a.body && <DialogDescription style={{ color: fg }}>{a.body}</DialogDescription>}
        </DialogHeader>
        <DialogFooter>
          {a.cta_url && a.cta_label && (
            <Button asChild>
              <a
                href={a.cta_url}
                target={a.cta_url.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
              >
                {a.cta_label}
              </a>
            </Button>
          )}
          <Button variant="outline" onClick={close}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
